import {
    collection,
    component,
    CustomObjectContextData,
    dateField,
    EntityData,
    field,
    identifier,
    LocalisableEntityData,
    localiser,
    transientField,
    unrestricted
} from "@fedeltapointofsale/data-client";
import {MarkupData} from "../../pricing/markup/markup.data";
import {PriceLevelReferenceData} from "../../pricing/price-level/price-level.data";
import {TaxReferenceData} from "../../system/tax/tax.data";
import {ProductContainerData} from "../product-container/product-container.data";
import {CategoryReferenceData} from "../category/category.data";
import {
    PreparationResourceTypeData
} from "../../order-processing/preparation-resource-type/preparation-resource-type.data";
import {MetaTagDefinitionReferenceData} from "../meta-tag/meta-tag.data";
import {DietaryIndicatorData} from "../dietary-indicator/dietary-indicator.data";
import {MembershipTypeReferenceData} from "../../customers/membership-type/membership-type.data";
import {PointsData} from "../../customers/points/points.data";
import {UncachedImageData} from "../../shared/image/uncached-image.data";

export class ProductPriceLevelData extends EntityData<ProductPriceLevelData> {
    @component markup: MarkupData;
    @localiser priceLevel: PriceLevelReferenceData;

    @transientField priceInc: number;
    @transientField priceEx: number;
    @transientField cost: number;
    @transientField profit: number;
    @transientField margin: number;
}

export class MemberTypeLoyaltySpecData extends EntityData<MemberTypeLoyaltySpecData> {
    @identifier id: number;
    @localiser membershipType: MembershipTypeReferenceData;
    @component accrual: PointsData;
    @component redemption: PointsData;
}

export class MemberLoyaltySpecData extends EntityData<MemberLoyaltySpecData> {
    @identifier id: number;
    @dateField newExpiry: Date;
    @field daysToExtendExpiryBy: number;
    @unrestricted @collection(MemberTypeLoyaltySpecData) memberTypeSpecs: MemberTypeLoyaltySpecData[];
}

export abstract class AbstractSellableData<T> extends LocalisableEntityData<T> {
    @identifier sellableId: number;
    @field name: string;
    @field receiptText: string;
    @field plu: string;
    @field price: number;
    @field baseCostEx: number;
    @field description: string;
    @field externalDescription: string;
    @field discontinued: boolean;
    @field usesScales: boolean;
    @field printScaleLabel: boolean;
    @field promptPrice: boolean;
    @field excludeFromPromotions: boolean;
    @field kitchenText: string;
    @field foreignKitchenText: string;
    @field printPriority: number;
    @field styleType: number;
    @field productImage: string;
    @field icon: string;
    @field mgtOnly: boolean;
    @field promptForDefaultType: number = 1;
    @field promptForDefaultPrice: number;
    @field kiloJoules: number;
    @field applyMinimumAge: boolean;
    @component primaryCategory: CategoryReferenceData;
    @collection(TaxReferenceData) taxes: TaxReferenceData[];
    @collection() barcodes: string[];
    @collection(PreparationResourceTypeData) preparationResourceTypes: PreparationResourceTypeData[];
    @collection(ProductContainerData) containers: ProductContainerData[];
    @collection(MetaTagDefinitionReferenceData) tags: MetaTagDefinitionReferenceData[];
    @unrestricted @collection(ProductPriceLevelData) priceLevels: ProductPriceLevelData[];
    @component customObjectContext: CustomObjectContextData;
    @collection(DietaryIndicatorData) dietaryIndicators: DietaryIndicatorData[];
    @component memberLoyalty: MemberLoyaltySpecData;
}

/**
 * This class is intended for use on the sellable search page.
 * Collections referencing a sellable should define their own version of this data as all use cases do not expect the same data subset.
 */
export class SellableData extends EntityData<SellableData> {
    public static readonly SELLABLE_TYPE_PRODUCT_LINE: string = "ProductLineData";
    public static readonly SELLABLE_TYPE_COMPOSITE_PRODUCT: string = "CompositeProductData";

    @field sellableType: string;
    @identifier sellableId: number;
    @field name: string;
    @field price: number;
    @field plu: string;
}

export class SellableReferenceData extends EntityData<SellableReferenceData> {
    @field sellableType: string;
    @identifier sellableId: number;
    @field name: string;
    @field discontinued: boolean;
}
